/**
 * 🔴 **WHAT A COMPANY *DOES* ON A PRODUCTION** (D57).
 *
 * Devin: *"network functions is a big one on the org level — we should have all the types like
 * agencies, lighting… think of all the network functions for the movie and entertainment industry."*
 *
 * Metaship's own list is a freight network's — Bank · Buyer · Coldstore · Consignee · Forwarder ·
 * Shipper · Shipping Line · Vessel Operator. Same idea, entirely different industry: this is the
 * film and television equivalent.
 *
 * 🔴 **A FUNCTION IS NOT A RELATIONSHIP AND NOT A ROLE.** Three different things, and conflating any
 * two of them is how a directory becomes unusable:
 *
 *   · **function** (here) — what this company DOES, in general. Permanent, plural, and true whether
 *     or not you have ever worked with them. Panavision is a camera rental house on every production
 *     that ever existed.
 *   · **participation** (D22) — what this company IS TO ONE PRODUCTION. A camera rental house can be
 *     the vendor on one show and a co-producer on another.
 *   · **role** (D41) — what a PERSON may do in an organisation. Nothing to do with either.
 *
 * So functions are a set on the company, they never expire, and nothing about access reads them.
 *
 * 🔴 **PLURAL BY DESIGN.** A post house that also does sound is both; a facility company that rents
 * lighting AND grip is both. Every real company on a call sheet is two or three of these, and a
 * single-select would force whoever types it to lie.
 *
 * The `group` exists so a picker of sixty is legible — nobody scans sixty chips, but everybody scans
 * eight headings. It carries no meaning beyond that.
 */

export type NetworkFunctionGroup =
  | "production"
  | "money"
  | "talent"
  | "crew"
  | "kit"
  | "locations"
  | "post"
  | "compliance";

export interface NetworkFunction {
  /** The stored value. Stable — a rename would silently unclassify every company that held it. */
  id: string;
  label: string;
  group: NetworkFunctionGroup;
  /** Material Symbols ligature, so a chip reads at a glance the way Metaship's does. */
  icon: string;
}

/**
 * 🔴 The vocabulary. Ordered by group, and within a group by how often a production actually books
 * one — a list sorted alphabetically puts "Animals" above "Production company", which is not how
 * anybody thinks about a slate.
 */
export const NETWORK_FUNCTIONS: ReadonlyArray<NetworkFunction> = [
  // ── Who makes it ────────────────────────────────────────────────────────
  { id: "production_company", label: "Production company", group: "production", icon: "movie" },
  { id: "co_producer", label: "Co-producer", group: "production", icon: "handshake" },
  { id: "production_services", label: "Production services", group: "production", icon: "support_agent" },
  { id: "studio", label: "Studio", group: "production", icon: "domain" },
  { id: "broadcaster", label: "Broadcaster", group: "production", icon: "live_tv" },
  { id: "streamer", label: "Streaming platform", group: "production", icon: "smart_display" },
  { id: "distributor", label: "Distributor", group: "production", icon: "share" },
  { id: "sales_agent", label: "Sales agent", group: "production", icon: "storefront" },

  // ── Who pays for it, and who guarantees it ──────────────────────────────
  { id: "financier", label: "Financier", group: "money", icon: "account_balance" },
  { id: "fund", label: "Film fund", group: "money", icon: "savings" },
  { id: "completion_guarantor", label: "Completion guarantor", group: "money", icon: "verified_user" },
  { id: "bank", label: "Bank", group: "money", icon: "account_balance" },
  { id: "payroll", label: "Payroll company", group: "money", icon: "payments" },
  { id: "production_accountant", label: "Production accountancy", group: "money", icon: "calculate" },
  { id: "insurer", label: "Insurer", group: "money", icon: "shield" },
  { id: "incentive_consultant", label: "Incentive & rebate consultancy", group: "money", icon: "redeem" },

  // ── Who is in it ────────────────────────────────────────────────────────
  { id: "talent_agency", label: "Talent agency", group: "talent", icon: "star" },
  { id: "casting", label: "Casting", group: "talent", icon: "how_to_reg" },
  { id: "background_agency", label: "Background & extras agency", group: "talent", icon: "groups" },
  { id: "management", label: "Management company", group: "talent", icon: "badge" },
  { id: "union", label: "Union or guild", group: "talent", icon: "gavel" },
  { id: "stunts", label: "Stunts", group: "talent", icon: "sports_martial_arts" },
  { id: "animals", label: "Animals & wrangling", group: "talent", icon: "pets" },

  // ── Who crews it ────────────────────────────────────────────────────────
  { id: "crew_agency", label: "Crew agency", group: "crew", icon: "engineering" },
  { id: "camera_department", label: "Camera", group: "crew", icon: "photo_camera" },
  { id: "lighting", label: "Lighting", group: "crew", icon: "light_mode" },
  { id: "grip", label: "Grip & rigging", group: "crew", icon: "construction" },
  { id: "sound", label: "Production sound", group: "crew", icon: "mic" },
  { id: "art_department", label: "Art department", group: "crew", icon: "palette" },
  { id: "set_construction", label: "Set construction", group: "crew", icon: "carpenter" },
  { id: "costume", label: "Costume & wardrobe", group: "crew", icon: "checkroom" },
  { id: "hair_makeup", label: "Hair & make-up", group: "crew", icon: "face_retouching_natural" },
  { id: "sfx", label: "Special effects", group: "crew", icon: "auto_fix_high" },

  // ── What they hire in ───────────────────────────────────────────────────
  { id: "equipment_rental", label: "Equipment rental", group: "kit", icon: "inventory_2" },
  { id: "picture_vehicles", label: "Picture vehicles", group: "kit", icon: "directions_car" },
  { id: "transport", label: "Transport", group: "kit", icon: "local_shipping" },
  { id: "facilities", label: "Facilities & trailers", group: "kit", icon: "rv_hookup" },
  { id: "catering", label: "Catering & craft", group: "kit", icon: "restaurant" },
  { id: "freight", label: "Freight & carnet", group: "kit", icon: "flight_takeoff" },

  // ── Where they shoot ────────────────────────────────────────────────────
  { id: "location_services", label: "Location services", group: "locations", icon: "map" },
  { id: "stage_rental", label: "Stage & backlot", group: "locations", icon: "warehouse" },
  { id: "security", label: "Security", group: "locations", icon: "local_police" },
  { id: "medical", label: "Medical & safety", group: "locations", icon: "medical_services" },
  { id: "travel", label: "Travel", group: "locations", icon: "travel_explore" },
  { id: "accommodation", label: "Accommodation", group: "locations", icon: "hotel" },

  // ── What happens after the shoot ────────────────────────────────────────
  { id: "post_house", label: "Post-production house", group: "post", icon: "movie_edit" },
  { id: "editorial", label: "Editorial", group: "post", icon: "content_cut" },
  { id: "vfx", label: "Visual effects", group: "post", icon: "auto_awesome" },
  { id: "grading", label: "Colour & grading", group: "post", icon: "gradient" },
  { id: "sound_post", label: "Sound post & mix", group: "post", icon: "graphic_eq" },
  { id: "music", label: "Music & score", group: "post", icon: "music_note" },
  { id: "music_licensing", label: "Music licensing", group: "post", icon: "copyright" },
  { id: "localisation", label: "Subtitling & dubbing", group: "post", icon: "translate" },
  { id: "titles", label: "Titles & graphics", group: "post", icon: "title" },

  // ── Who has to say yes ──────────────────────────────────────────────────
  { id: "film_commission", label: "Film commission", group: "compliance", icon: "flag" },
  { id: "permit_authority", label: "Permits & authority", group: "compliance", icon: "approval" },
  { id: "classification", label: "Classification board", group: "compliance", icon: "policy" },
  { id: "collecting_society", label: "Collecting society", group: "compliance", icon: "receipt_long" },
  { id: "legal", label: "Legal & clearance", group: "compliance", icon: "balance" },
  { id: "archive", label: "Archive & stock footage", group: "compliance", icon: "inventory" },
];

/** Small capitals over each run of chips — sixty chips are unreadable, eight headings are not. */
export const NETWORK_FUNCTION_GROUP_LABELS: Readonly<Record<NetworkFunctionGroup, string>> = {
  production: "Who makes it",
  money: "Money & guarantees",
  talent: "Talent",
  crew: "Crew & departments",
  kit: "Kit & logistics",
  locations: "Locations & travel",
  post: "Post-production",
  compliance: "Compliance & rights",
};

const IDS = new Set(NETWORK_FUNCTIONS.map((entry) => entry.id));

/** Unknown ids are refused, never stored: a typo would become a company nobody can find by function. */
export const isNetworkFunction = (value: unknown): boolean => typeof value === "string" && IDS.has(value);

/** The functions in their declared order, whatever order they were stored or typed in. */
export function orderNetworkFunctions(ids: ReadonlyArray<string>): NetworkFunction[] {
  const held = new Set(ids.filter(isNetworkFunction));
  return NETWORK_FUNCTIONS.filter((entry) => held.has(entry.id));
}
